import {
  Autocomplete, Box, Button, Divider, Paper,
  TextField, Typography, useTheme
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiBox, FiCheck, FiChevronLeft, FiChevronRight, FiColumns, FiMapPin, FiSend, FiTarget } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { MainContainer } from "../../components/container/MainContainer";
import TextBox from "../../components/input/TextBox";
import ResizableTextBox from "../../components/input/ResizableTextBox";
import SelectBox from "../../components/input/SelectBox";
import { Emission } from "../../db_access/emissions";
import { Partnership } from "../../db_access/partnership";
import { Product } from "../../db_access/product";
import { Project } from "../../db_access/project";
import { Structure } from "../../db_access/structure";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import "../../style/pages-style/Engagement/engagement.css";
import { scope1Categories, scope2Categories, scope3CategoriesForSupplyChain } from "../Organization/ScopeCategory";
import routing from "../../routing";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

/* ─── wizard steps ──────────────────────────────────────────────── */
const STEPS = ["Choose Data Level", "Configure Report", "Select Recipient"];

/* ─── data level tiles ──────────────────────────────────────────── */
const LEVELS = [
  { value: "product_level", label: "Product Level", Icon: FiBox, desc: "Share a specific product's carbon footprint" },
  { value: "project_level", label: "Project Level", Icon: FiColumns, desc: "Share a construction or design project's data" },
  { value: "facility_level", label: "Facility Level", Icon: FiMapPin, desc: "Share a facility or building's emission data" },
];

const PRIMARY_COLOR = ORG_PALETTE.segmentedTabs.activeColor;
const PRIMARY_LIGHT = ORG_PALETTE.scopeBadge.avoided.bg;
const PRIMARY_DARK = "#15803d";

const Supplier_Directory_SubmitData = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const defaultData = useMemo(() => ({
    year: "", quantity: "", functional_unit: "", other_unit: "",
    gwp_per_unit: "", cost_per_unit: "", gwp: 0, cost: 0, name: "",
  }), []);

  const [step, setStep] = useState(0);
  const [data, setData] = useState(defaultData);
  const [level, setLevel] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [scope, setScope] = useState(null);
  const [notes, setNotes] = useState("");
  const [partners, setPartners] = useState([]);
  const [receiver, setReceiver] = useState(null);
  const [emissions, setEmissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setDataField = (field) => (e) => setData((p) => ({ ...p, [field]: e?.target?.value }));
  const isDataLevel = useCallback((v) => level === v, [level]);

  /* ── scope options ─────────────────────────────────────────────── */
  const scope_all = useMemo(() => ({ label: "All Scopes", value: "scope_all", filter: () => true }), []);
  const scope_1 = useMemo(() => ({ label: "Scope 1", value: "scope_1", filter: (i) => !i.is_avoided && scope1Categories.includes(i.category) }), []);
  const scope_2 = useMemo(() => ({ label: "Scope 2", value: "scope_2", filter: (i) => !i.is_avoided && scope2Categories.includes(i.category) }), []);
  const scope_3 = useMemo(() => ({ label: "Scope 3", value: "scope_3", filter: (i) => !i.is_avoided && scope3CategoriesForSupplyChain.includes(i.category) }), []);
  const scopes = useMemo(() => [scope_all, scope_1, scope_2, scope_3], [scope_all, scope_1, scope_2, scope_3]);

  /* ── fetch options by level ────────────────────────────────────── */
  useEffect(() => {
    setSelectedOption(null); setOptions([]);
    if (level === "product_level") {
      Product.get_mine().then((r) => r.json()).then((data) =>
        setOptions(data.map((d) => ({
          ...d, name: d.product || d.name || "Product",
          label: (d.product || d.name || "Product") + " " + d.year,
          value: d.id, gwp_per_unit: d.gwp || 0, cost_per_unit: d.cost || 0,
          gwp: (d.gwp || 0) * (d.quantity || 1), cost: (d.cost || 0) * (d.quantity || 1),
          functional_unit: d.functional_unit || "", other_unit: d.other_unit || "",
        })))
      );
    } else if (level === "project_level") {
      Project.get_mine().then((r) => r.json()).then((data) =>
        setOptions(data.map((d) => ({
          ...d, name: d.design_installation || d.name || "Project",
          label: (d.design_installation || d.name || "Project") + " " + d.year,
          value: d.id, gwp_per_unit: d.gwp || 0, cost_per_unit: d.cost || 0,
          gwp: (d.gwp || 0) * (d.quantity || 1), cost: (d.cost || 0) * (d.quantity || 1),
          functional_unit: d.functional_unit || "", other_unit: d.other_unit || "",
        })))
      );
    } else if (level === "facility_level") {
      Structure.get_mine().then((r) => r.json()).then((data) =>
        setOptions(data.map((d) => ({
          ...d, name: d.name || d.organization || "Facility",
          label: (d.name || d.organization || "Facility") + " " + d.year,
          value: d.id, gwp_per_unit: d.gwp || 0, cost_per_unit: d.cost || 0,
          gwp: (d.gwp || 0) * (d.quantity || 1), cost: (d.cost || 0) * (d.quantity || 1),
          functional_unit: d.functional_unit || "", other_unit: d.other_unit || "",
        })))
      );
    }
  }, [level]);

  useEffect(() => {
    if (!selectedOption) { setData(defaultData); setEmissions([]); return; }
    setData(selectedOption);
    if (isDataLevel("facility_level")) {
      Emission.getEmissionsByStructureId(selectedOption.id).then((r) => r.json()).then(setEmissions);
    }
  }, [selectedOption, defaultData, isDataLevel]);

  useEffect(() => {
    if (scopes.map((s) => s.value).includes(scope)) {
      const f = scopes.find((s) => s.value === scope).filter;
      const gwp_per_unit = emissions.filter(f).reduce((sum, e) => sum + (e.gwp || 0), 0);
      setData((prev) => ({ ...prev, gwp_per_unit }));
    }
  }, [scope, emissions, scopes]);

  useEffect(() => {
    setData((p) => ({ ...p, gwp: (p.gwp_per_unit || 0) * (p.quantity || 0), cost: (p.cost_per_unit || 0) * (p.quantity || 0) }));
  }, [data.gwp_per_unit, data.cost_per_unit, data.quantity]);

  useEffect(() => {
    Partnership.get_my_partners().then((r) => r.json()).then((data) => {
      if (data) setPartners(data.map((d) => ({ label: d.name, value: d.id })));
    });
  }, []);

  /* ── submit ───────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await Supplier_Reports.new({
        ...data, level, scope: scope === "scope_all" ? null : scope,
        name: data.name, notes, receiver_id: receiver?.value,
      });
      setSubmitted(true);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  /* ── step validation ──────────────────────────────────────────── */
  const canAdvance = [
    () => !!level,
    () => !!selectedOption && !!data.year && !!data.quantity,
    () => !!receiver,
  ];

  if (submitted) {
    return (
      <MainContainer>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          <Paper elevation={0} sx={{ p: 6, borderRadius: ORG_PALETTE.radius.card, border: "1.5px solid", borderColor: tok.border.card, textAlign: "center", maxWidth: 500, width: "100%", background: tok.bg.card }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", background: "#dcfce7", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3 }}>
              <FiCheck size={40} />
            </Box>
            <Typography sx={{ fontFamily: "Poppins", fontSize: 24, fontWeight: 800, color: "#111827", mb: 1 }}>
              Report Sent Successfully!
            </Typography>
            <Typography sx={{ fontFamily: "Poppins", fontSize: 14, color: "#64748b", mb: 4, lineHeight: 1.6 }}>
              Your carbon data report has been shared with <strong>{receiver?.label}</strong>. It is now awaiting their review and acceptance.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={() => { setSubmitted(false); setStep(0); setLevel(null); setData(defaultData); setReceiver(null); setNotes(""); }}
                sx={{
                  backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" },
                  fontFamily: "Poppins", fontSize: 13, fontWeight: 600, textTransform: "none", borderRadius: "12px", py: 1.5
                }}>
                Submit Another Report
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(routing.engagement.supplier_directory.main)}
                sx={{
                  color: "#64748b", borderColor: "#e2e8f0", "&:hover": { borderColor: "#16a34a", color: "#16a34a", background: "#f0fdf4" },
                  fontFamily: "Poppins", fontSize: 13, fontWeight: 600, textTransform: "none", borderRadius: "12px", py: 1.5
                }}>
                View Dashboard
              </Button>
            </Box>
          </Paper>
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      {/* ── Page Header ────────────────────────────────────────── */}
      <Box sx={{ animation: "eng-fadeInUp 0.4s ease", textAlign: "left", mb: ORG_PALETTE.spacing.pageGap }}>
        <Typography variant="h5" sx={{ fontFamily: ORG_PALETTE.font.family, fontWeight: ORG_PALETTE.font.weight.semibold, color: tok.text.primary }}>
          {t("Submit Data to Client")}
        </Typography>
        <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 14, color: tok.text.chartSub, mt: 0.5 }}>
          {t("Share your carbon footprint data with one of your clients")}
        </Typography>
      </Box>

      {/* ── Step Progress Bar ───────────────────────────────────── */}
      <div className="eng-step-bar" style={{ marginBottom: ORG_PALETTE.spacing.pageGap }}>
        {STEPS.map((label, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : 0 }}>
            <div className={`eng-step-item ${i === step ? "active" : i < step ? "done" : ""}`}
              style={{ color: i === step ? PRIMARY_COLOR : tok.text.secondary }}>
              <div className="eng-step-dot"
                style={i === step ? { background: PRIMARY_COLOR, borderColor: PRIMARY_COLOR, color: "white" } :
                  i < step ? { background: PRIMARY_LIGHT, borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR } : { borderColor: tok.border.card, color: tok.text.secondary }}>
                {i < step ? <FiCheck size={12} /> : i + 1}
              </div>
              <span style={{ whiteSpace: "nowrap", fontFamily: ORG_PALETTE.font.family }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: i < step ? PRIMARY_COLOR : tok.border.card, margin: "0 8px" }} />
            )}
          </Box>
        ))}
      </div>

      <Paper elevation={0} sx={{ border: "1.5px solid", borderColor: tok.border.card, background: tok.bg.card, borderRadius: ORG_PALETTE.radius.card, p: ORG_PALETTE.spacing.cardPadding, minHeight: 380 }}>

        {/* ── Step 0: Choose Data Level ───────────────────────── */}
        {step === 0 && (
          <Box className="eng-detail-panel" sx={{ textAlign: "center", pt: 5, pb: 5 }}>
            <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 24, fontWeight: 400, mb: 1, color: tok.text.primary }}>
              What type of data are you submitting?
            </Typography>
            <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 15, color: tok.text.chartSub, mb: 5 }}>
              Choose the level that best describes the data you want to share
            </Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center", maxWidth: "900px", mx: "auto" }}>
              {LEVELS.map(({ value, label, Icon, desc }, i) => (
                <Box
                  key={value}
                  className={`eng-level-tile ${level === value ? "selected" : ""}`}
                  style={{
                    flex: "1 1 240px", minHeight: "220px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
                    animationDelay: `${i * 0.08}s`,
                    border: `1.5px solid ${level === value ? PRIMARY_COLOR : tok.border.card}`,
                    background: level === value ? (isDark ? 'rgba(22,163,74,0.05)' : '#f8fafc') : (isDark ? tok.bg.main : '#ffffff'),
                    borderRadius: ORG_PALETTE.radius.card, cursor: "pointer", padding: "32px 24px",
                    boxShadow: level === value ? "0 4px 12px rgba(22,163,74,0.1)" : "none"
                  }}
                  onClick={() => setLevel(value)}>
                  <div className="eng-level-tile__icon" style={{ marginBottom: "20px" }}>
                    <Icon size={46} color={level === value ? PRIMARY_COLOR : tok.text.secondary} />
                  </div>
                  <div className="eng-level-tile__label"
                    style={{ color: tok.text.primary, fontFamily: ORG_PALETTE.font.family, fontSize: "17px", fontWeight: 400, marginBottom: "10px" }}>{label}</div>
                  <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 13, color: tok.text.chartSub, textAlign: "center", lineHeight: 1.5 }}>{desc}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Step 1: Configure Report ────────────────────────── */}
        {step === 1 && (
          <Box className="eng-detail-panel" sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 22, fontWeight: 400, mb: 1, color: tok.text.primary }}>Configure Your Report</Typography>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 14, color: tok.text.chartSub }}>Select the item and fill in the quantities</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, "& .MuiOutlinedInput-root": { background: isDark ? tok.bg.main : "#f8fafc", borderRadius: ORG_PALETTE.radius.card, fieldset: { border: "1.5px solid #e2e8f0" } } }}>
              <Box>
                <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 11, fontWeight: 400, color: tok.text.secondary, mb: 1, letterSpacing: 0.8, textTransform: "uppercase" }}>SELECT ITEM</Typography>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 15, fontWeight: 400, color: tok.text.primary }}>
                    {LEVELS.find((l) => l.value === level)?.label || "Select"}
                  </Typography>
                </Box>
                {options.length > 0 ? (
                  <SelectBox
                    placeholder="Choose an item…" options={options} freeSolo={false}
                    onChange={(_, v) => setSelectedOption(v)} value={selectedOption} />
                ) : (
                  <Box sx={{ p: 2, border: `1.5px dashed ${tok.border.card}`, borderRadius: ORG_PALETTE.radius.card, textAlign: "center", background: tok.bg.main }}>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 13, color: tok.text.chartSub }}>
                      No items found — create one in the {LEVELS.find((l) => l.value === level)?.label} section first
                    </Typography>
                  </Box>
                )}
              </Box>

              {level === "facility_level" && (
                <Box>
                  <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 11, fontWeight: 400, color: tok.text.secondary, mb: 1, letterSpacing: 0.8, textTransform: "uppercase" }}>SCOPE</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 15, fontWeight: 400, color: tok.text.primary }}>Scope Filter</Typography>
                  </Box>
                  <SelectBox
                    placeholder="Select scope" options={scopes} freeSolo={false}
                    onChange={(_, v) => setScope(v?.value)} value={scope} />
                </Box>
              )}

              <Divider />

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 3 }}>
                {[
                  { label: "Year", field: "year" }, { label: "Quantity", field: "quantity" },
                  { label: "Functional Unit", field: "functional_unit" }, { label: "Other Unit", field: "other_unit" },
                  { label: "GWP per Unit", field: "gwp_per_unit" }, { label: "Cost per Unit", field: "cost_per_unit" },
                ].map((f) => (
                  <TextBox key={f.field} label={f.label} placeholder={f.label}
                    value={data?.[f.field]} onChange={setDataField(f.field)} />
                ))}
              </Box>

              {/* Computed totals */}
              <Box sx={{ display: "flex", gap: 3 }}>
                {[{ label: "Total GWP", field: "gwp", unit: "kg CO₂-eq", color: PRIMARY_COLOR, bg: isDark ? PRIMARY_LIGHT : tok.bg.main },
                { label: "Total Cost", field: "cost", unit: "₹", color: tok.text.primary, bg: isDark ? tok.bg.card : tok.bg.main }].map((f) => (
                  <Box key={f.field} sx={{ flex: 1, p: 3, pb: 2, background: f.bg, borderRadius: ORG_PALETTE.radius.card, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", border: "1px solid", borderColor: tok.border.card }}>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 11, color: tok.text.chartSub, fontWeight: 400, letterSpacing: 0.8 }}>{f.label.toUpperCase()}</Typography>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 26, fontWeight: ORG_PALETTE.font.weight.heavy, color: f.color, mt: 1, mb: 0.5 }}>
                      {Math.round(data?.[f.field] || 0).toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 12, color: tok.text.chartSub }}>{f.unit}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Notes */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 13, fontWeight: 400, color: tok.text.primary }}>
                    Additional Notes
                  </Typography>
                </Box>
                <ResizableTextBox placeholder="Optional notes for the recipient…"
                  onChange={(e) => setNotes(e.target.value)} />
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Step 2: Select Recipient ────────────────────────── */}
        {step === 2 && (
          <Box className="eng-detail-panel" sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 22, fontWeight: 400, mb: 1, color: tok.text.primary }}>
                Who are you submitting to?
              </Typography>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 14, color: tok.text.chartSub }}>
                Choose the partner you want to send this report to
              </Typography>
            </Box>

            {/* Summary card */}
            <Box sx={{ p: 4, background: isDark ? tok.bg.main : "#f8fafc", borderRadius: ORG_PALETTE.radius.card, mb: 4, border: "1px solid", borderColor: tok.border.card }}>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 11, fontWeight: 400, letterSpacing: 0.8, color: PRIMARY_COLOR, mb: 3, textAlign: "center" }}>
                REPORT SUMMARY
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 3, textAlign: "center" }}>
                {[
                  { label: "Level", val: LEVELS.find((l) => l.value === level)?.label },
                  { label: "Item", val: selectedOption?.name || selectedOption?.label },
                  { label: "Year", val: data.year },
                  { label: "Quantity", val: data.quantity },
                  { label: "GWP", val: `${Math.round(data.gwp || 0).toLocaleString()} kg CO₂-eq` },
                  { label: "Cost", val: `₹ ${Math.round(data.cost || 0).toLocaleString()}` },
                ].map((f) => (
                  <Box key={f.label}>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 11, color: tok.text.chartSub, fontWeight: 400, letterSpacing: 0.8 }}>{f.label.toUpperCase()}</Typography>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 14, fontWeight: 400, color: tok.text.primary, mt: 1 }}>{f.val || "—"}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 11, fontWeight: 400, letterSpacing: 0.8, color: tok.text.secondary, mb: 1 }}>
              SELECT PARTNER
            </Typography>
            <Autocomplete
              disablePortal
              options={partners}
              value={receiver}
              onChange={(_, v) => setReceiver(v)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search your partners…"
                  sx={{
                    "& .MuiOutlinedInput-root": { background: isDark ? tok.bg.main : "#f3f4f6", borderRadius: "12px", fieldset: { border: "none" } },
                    "& .MuiInputBase-root": { fontFamily: ORG_PALETTE.font.family, fontSize: 14 },
                  }} />
              )}
              PaperComponent={(props) => <Paper {...props} sx={{ fontFamily: ORG_PALETTE.font.family, borderRadius: ORG_PALETTE.radius.card, mt: 0.5 }} />}
            />

            {partners.length === 0 && (
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 12, color: "#f59e0b", mt: 1 }}>
                ⚠ You have no partners yet. Go to B2B Directory to add one first.
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* ── Navigation Buttons ──────────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: ORG_PALETTE.spacing.pageGap }}>
        <Button variant="outlined" startIcon={<FiChevronLeft />}
          onClick={() => setStep((s) => s - 1)} disabled={step === 0}
          sx={{
            fontFamily: ORG_PALETTE.font.family, fontSize: 13, fontWeight: ORG_PALETTE.font.weight.semibold, textTransform: "none",
            borderRadius: ORG_PALETTE.radius.button, borderColor: tok.border.input, color: tok.text.secondary,
            "&:hover": { borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR }
          }}>
          Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button variant="contained" endIcon={<FiChevronRight />}
            onClick={() => setStep((s) => s + 1)} disabled={!canAdvance[step]?.()}
            sx={{
              backgroundColor: PRIMARY_COLOR, "&:hover": { backgroundColor: PRIMARY_DARK },
              fontFamily: ORG_PALETTE.font.family, fontSize: 13, fontWeight: ORG_PALETTE.font.weight.semibold, textTransform: "none", borderRadius: ORG_PALETTE.radius.button
            }}>
            Next
          </Button>
        ) : (
          <Button variant="contained" startIcon={submitting ? null : <FiSend size={14} />}
            onClick={handleSubmit} disabled={!receiver || submitting}
            sx={{
              backgroundColor: PRIMARY_COLOR, "&:hover": { backgroundColor: PRIMARY_DARK },
              fontFamily: ORG_PALETTE.font.family, fontSize: 13, fontWeight: ORG_PALETTE.font.weight.semibold, textTransform: "none", borderRadius: ORG_PALETTE.radius.button
            }}>
            {submitting ? "Submitting…" : "Submit Report"}
          </Button>
        )}
      </Box>
    </MainContainer>
  );
};

export default Supplier_Directory_SubmitData;
